import { useFieldArray, useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  FormAutocomplete,
  AutocompleteOption,
} from '@/components/ui/custom/Autocomplete/Autocomplete';
import i18n from '@/i18n/i18n';
import { cn } from '@/lib/utils';
import { useSubscribers } from '@/modules/broadcast/hooks/useSubscribers';
import {
  selectBroadcast,
  selectContextVariableOptions,
  selectIsDraft,
} from '@/modules/broadcast/redux/selectors';
import { getContextVariableOptions } from '@/modules/broadcast/utils';
import { extractVariableNames } from '@/modules/whatsapp/utils';
import { Form, WhatsappTemplate } from '@/shared/types/broadcast';
import { Option } from '@/shared/types/rules';

interface Props {
  name: 'body' | 'header' | 'footer';
  isReadOnly: boolean;
  template: WhatsappTemplate;
}

/**
 * Renders a variable field with combobox
 */
interface VariableFieldProps {
  field: { id: string; [key: string]: unknown };
  index: number;
  formName: 'headerParameters' | 'bodyParameters' | 'footerParameters';
  variableNames: string[];
  template: WhatsappTemplate;
  options: AutocompleteOption[];
  control: ReturnType<typeof useFormContext<Form>>['control'];
  errors: ReturnType<typeof useFormContext<Form>>['formState']['errors'];
  isReadOnly: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
}

const VariableField = ({
  field,
  index,
  formName,
  variableNames,
  template,
  options,
  control,
  errors,
  isReadOnly,
  t,
}: VariableFieldProps) => {
  // Get variable name based on parameter format
  const varDisplay =
    template.parameter_format === 'NAMED' && index < variableNames.length
      ? `{{${variableNames[index]}}}`
      : `{{${index + 1}}}`; // Fallback to positional parameters

  return (
    <div key={field.id} className="flex w-[292px] items-center">
      <div className="-mr-0.5 flex h-10 w-fit items-center justify-center rounded-l rounded-tr-none rounded-br-none border border-gray-300 bg-neutral-50 p-2 text-sm text-ellipsis text-gray-700">
        {varDisplay}
      </div>
      <div className="w-[292px]">
        <FormAutocomplete<Form>
          control={control}
          name={`${formName}.${index}.name`}
          options={options}
          placeholder={t('broadcasts.select_variable', { 0: varDisplay })}
          rules={{ required: t('validation.required') }}
          error={Boolean(errors[formName]?.[index]?.name)}
          errorMessage={errors[formName]?.[index]?.name?.message}
          disabled={isReadOnly}
          className={cn('[&_button]:rounded-l-none [&_button]:border-l-0')}
        />
      </div>
    </div>
  );
};

/**
 * Transform Option[] to AutocompleteOption[]
 * Maps the `type` field to `category` for grouping
 */
function transformOptions(options: Option[]): AutocompleteOption[] {
  return options.map((opt) => ({
    value: opt.value ?? '',
    label: opt.label ?? opt.value ?? '',
    category: i18n.t(`auto_complete.group_labels.${opt.type}`),
  }));
}

export const VariablesSection = ({ name, isReadOnly, template }: Props) => {
  const { t } = useTranslation();
  const { control } = useFormContext<Form>();
  const { errors } = useFormState({ control });
  const isDraft = useSelector(selectIsDraft);
  const broadcast = useSelector(selectBroadcast);
  const { subscribers } = useSubscribers(broadcast.broadcast_id);
  const draftSubscribersOptions = useSelector(selectContextVariableOptions);
  const storedSubscribersOptions = getContextVariableOptions(subscribers?.pages?.[0].subscribers);
  const rawOptions = isDraft ? draftSubscribersOptions : storedSubscribersOptions;
  const options = transformOptions(rawOptions);
  const formName = `${name}Parameters` as const;

  const { fields } = useFieldArray({ control, name: formName });

  // Extract variable names based on template parameters
  const variableNames = extractVariableNames(template, name);

  return (
    <div className="mb-4">
      <p className="text-sm leading-5 font-semibold">{t('try_it.add_variables')}</p>
      <span className="text-sm leading-5 font-normal">{t('broadcasts.broadcast_variables')}</span>
      <div className="my-2 flex flex-wrap gap-4">
        {fields.map((field, index) => (
          <VariableField
            key={field.id}
            field={field}
            index={index}
            formName={formName}
            variableNames={variableNames}
            template={template}
            options={options}
            control={control}
            errors={errors}
            isReadOnly={isReadOnly}
            t={t}
          />
        ))}
      </div>
    </div>
  );
};
